import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload } from "lucide-react";

// Simple CSV parser: header row required. Columns: name,email,phone,start_date,status
function parseCsv(text) {
  const rows = text.split(/\r?\n/).map((r) => r.trim()).filter(Boolean);
  if (rows.length === 0) return [];
  const headers = rows[0].split(",").map((h) => h.trim().toLowerCase());
  const out = [];
  for (let i = 1; i < rows.length; i++) {
    const cells = rows[i].split(",").map((c) => c.trim());
    if (!cells.some(Boolean)) continue;
    const obj = {};
    headers.forEach((h, idx) => (obj[h] = cells[idx] || ""));
    out.push({
      name: obj.name || "",
      email: obj.email || "",
      phone: obj.phone || "",
      start_date: obj.start_date ? new Date(obj.start_date).toISOString() : null,
      status: obj.status || "Getting Started",
      create_login: true,
    });
  }
  return out;
}

export default function BulkImportDialog({ onImported }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("name,email,phone,start_date,status\nJane Doe,jane@example.com,555-0101,2026-06-01,Started\n");
  const [defaultPassword, setDefaultPassword] = useState("ClientPass123!");
  const [busy, setBusy] = useState(false);
  const rows = parseCsv(csv);

  const submit = async () => {
    if (rows.length === 0) return toast.error("No rows detected in CSV");
    const invalid = rows.filter((r) => !r.name || !r.email);
    if (invalid.length) return toast.error("All rows require name & email columns");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clients/bulk-import", { rows, default_password: defaultPassword });
      toast.success(`Imported ${data.created} of ${data.total}${data.skipped.length ? ` (${data.skipped.length} skipped)` : ""}`);
      setOpen(false);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const onFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setCsv(text);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-11 rounded-xl" data-testid="bulk-import-btn">
          <Upload size={16} className="mr-1" /> Bulk import
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Bulk import clients (CSV)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            Headers: <span className="font-mono text-xs">name,email,phone,start_date,status</span>. Rows create client + login accounts with the default password below (each user is forced to change it on first sign-in).
          </p>
          <div>
            <Label>Upload .csv</Label>
            <Input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files[0])} data-testid="bulk-csv-file" />
          </div>
          <div>
            <Label>Or paste CSV</Label>
            <Textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={7} className="font-mono text-xs" data-testid="bulk-csv-textarea" />
          </div>
          <div>
            <Label>Default temporary password</Label>
            <Input value={defaultPassword} onChange={(e) => setDefaultPassword(e.target.value)} data-testid="bulk-default-password" />
          </div>
          <div className="text-xs text-slate-500">Detected rows: <span className="font-semibold text-slate-800" data-testid="bulk-row-count">{rows.length}</span></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || rows.length === 0} className="bg-amber-500 hover:bg-amber-600" data-testid="bulk-import-submit">
            {busy ? "Importing…" : `Import ${rows.length} client${rows.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
